import React, { Fragment, useState } from 'react';

import { dashboardPartnerApis } from '@apis/next/partnerApis';
import { FormTitle } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { PartnerFormDialog } from '@components/admin/widgets/PartnerFormDialog';
import { partnerStrings } from '@data/partners';
import { useSearch } from '@hooks/next/useSearch';
import Collapse from '@material-ui/core/Collapse';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getBreadcrumb } from '@utils/pages';
import Color from 'color';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: `${partnerStrings.partner}詳細`,
    companyDetails: '業者情報',
    contacts: `${partnerStrings.contacts}一覧`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    flex: 1,
    padding: '1.5rem 2rem',
  },
  partnerInfoContainer: {
    ...common.middleText,
    ...common.textGray800,
    display: 'grid',
    gridTemplateColumns: '12fr 38fr 12fr 38fr',
    border: `1px solid ${Color(theme.palette.primary.main).alpha(0.1).toString()}`,
    '& > div': {
      padding: '15px 20px',
    },
    '& > *[role="head"]': {
      ...common.flexColumnCenter,
      ...common.fontWeight600,
      textAlign: 'center',
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
    '& > *[role="data"]': {
      position: 'relative',
    },
    '& *[role="divider"]': {
      height: 1,
      width: '100%',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  contactsContainer: {
    ...common.middleText,
    ...common.textGray800,
    display: 'grid',
    gridTemplateColumns: 'repeat(5, auto)',
    marginBottom: '1rem',
    '& > div': {
      paddingLeft: 22,
      paddingRight: 22,
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '& > *[role="head"]': {
      ...common.fontWeight600,
      backgroundColor: theme.palette.grey[50],
      paddingTop: 15,
      paddingBottom: 15,
    },
    '& > *[role="data"]': {
      paddingTop: 12,
      paddingBottom: 12,
    },
  },
}));

export const PartnerDetails: React.FC = () => {
  const { search } = useSearch(z.object({ id: z.number() }));

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [partnerInfoOpen, setPartnerInfoOpen] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(true);

  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(false);

  const partnerQuery = dashboardPartnerApis.useGet('/dashboard/partner/:id', {
    params: { id: search.id },
  });

  const item = partnerQuery.data!;

  const renderPartnerInfo = () => {
    return (
      <div className={classes.partnerInfoContainer}>
        <div role="head">{partnerStrings.type}</div>
        <div role="data" style={{ columnSpan: 'all' }}>
          {item.type.join('/') || '-'}
          <div role="divider" />
        </div>
        <div role="data">
          <div role="divider" />
        </div>
        <div role="data">
          <div role="divider" />
        </div>
        <div role="head">{partnerStrings.short_name_en}</div>
        <div role="data">
          {item.name_en_short || '-'}
          <div role="divider" />
        </div>
        <div role="head">{partnerStrings.fcNo}</div>
        <div role="data">
          {item.fc_no || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.name_ja}</div>
        <div role="data">
          {item.name_ja || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.name_en}</div>
        <div role="data">
          {item.name_en || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.registrationNumber}</div>
        <div role="data">
          {item.registration_number || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.corporateNumber}</div>
        <div role="data">
          {item.corporate_number || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.tel}</div>
        <div role="data">
          {item.tel || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.fax}</div>
        <div role="data">
          {item.fax || '-'}
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.address}</div>
        <div role="data">
          <div role="data">{item.address || '-'}</div>
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.paymentConditions}</div>
        <div role="data">
          <div role="data">
            <div>
              {partnerStrings.genbarai}
              {'　　'}
              {partnerStrings.cutoffDate} {item.payment_conditions.現払い.cutoff_date_month}
              {item.payment_conditions.現払い.cutoff_date_day === '日締' &&
                item.payment_conditions.現払い.cutoff_date_selected_day}
              {item.payment_conditions.現払い.cutoff_date_day}
            </div>
            <div>
              {'　　　　'}
              {partnerStrings.paymentDate} {item.payment_conditions.現払い.payment_date_month}
              {item.payment_conditions.現払い.payment_date_day === '日払' &&
                item.payment_conditions.現払い.payment_date_selected_day}
              {item.payment_conditions.現払い.payment_date_day}
            </div>
            <div>
              {partnerStrings.shimebarai}
              {'　'}
              {partnerStrings.cutoffDate} {item.payment_conditions.締払い.cutoff_date_month}
              {item.payment_conditions.締払い.cutoff_date_day === '日締' &&
                item.payment_conditions.締払い.cutoff_date_selected_day}
              {item.payment_conditions.締払い.cutoff_date_day}
            </div>
            <div>
              {'　　　　'}
              {partnerStrings.paymentDate} {item.payment_conditions.締払い.payment_date_month}
              {item.payment_conditions.締払い.payment_date_day === '日払' &&
                item.payment_conditions.締払い.payment_date_selected_day}
              {item.payment_conditions.締払い.payment_date_day}
            </div>
          </div>
          <div role="divider" />
        </div>

        <div role="head">{partnerStrings.memo1}</div>
        <div role="data">{item.memo1 || '-'}</div>

        <div role="head">{partnerStrings.memo2}</div>
        <div role="data">{item.memo2 || '-'}</div>
      </div>
    );
  };

  const renderContacts = () => {
    return (
      <div className={classes.contactsContainer}>
        <div role="head">NO.</div>
        <div role="head">{partnerStrings.contacts}</div>
        <div role="head">{partnerStrings.email}</div>
        <div role="head">{partnerStrings.phone}</div>
        <div role="head" />

        {item.contacts.map((contact, index) => (
          <Fragment key={index}>
            <div role="data">{index + 1}</div>
            <div role="data">{contact.name || '-'}</div>
            <div role="data">{contact.email || '-'}</div>
            <div role="data">{contact.phone || '-'}</div>
            <div role="data">
              <a
                className={classes.link}
                onClick={() => {
                  setEditContact(true);
                  setPartnerDialogOpen(true);
                }}
              >
                {formStrings.edit}
              </a>
            </div>
          </Fragment>
        ))}
      </div>
    );
  };

  return (
    <Layout
      title={strings.title}
      loading={partnerQuery.isLoading}
      errors={[partnerQuery.error]}
      breadcrumb={[...getBreadcrumb('/admin/widgets/Partners'), item?.name_ja ?? '-']}
    >
      {() => (
        <div className={classes.container}>
          <PartnerFormDialog
            type="update"
            open={partnerDialogOpen}
            onClose={() => {
              setPartnerDialogOpen(false);
              partnerQuery.invalidate();
            }}
            partnerId={search.id}
            editContact={editContact}
          />

          <FormTitle
            title={strings.companyDetails}
            noExpand={false}
            expanded={partnerInfoOpen}
            onChangeExpanded={setPartnerInfoOpen}
            onChangeEditting={() => {
              setEditContact(false);
              setPartnerDialogOpen(true);
            }}
          />
          <Collapse in={partnerInfoOpen}>{renderPartnerInfo()}</Collapse>

          <div style={{ height: 30 }} />

          <FormTitle
            title={strings.contacts}
            noExpand={false}
            expanded={contactsOpen}
            onChangeExpanded={setContactsOpen}
            noEdit
          />
          <Collapse in={contactsOpen}>{renderContacts()}</Collapse>
        </div>
      )}
    </Layout>
  );
};
